import { inject, Injectable, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { defer, map, Observable, tap } from 'rxjs';
import { ContactSalesLead, PurchaseSubscriptionModel, ValidateVatResponse } from '../models/paywall-settings';
import { HttpClient } from '@angular/common/http';
import { PaywallService } from './paywall.service';
import packageJson from '../../../package.json';

export const SESSION_TOKEN_HEADER = 'X-Paywall-Session-Token';
export const PREVIEW_TOKEN_HEADER = 'X-Paywall-Preview-Token';

export interface PurchaseSubscriptionResponse {
  sessionId: string;
  sessionToken: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  paywallService = inject(PaywallService);
  private hubConnection?: signalR.HubConnection;
  private starting?: Promise<void>;
  // Kept here so they move along when the connection is replaced after a back/forward cache restore.
  private readonly handlers: { method: string; handler: (message: any) => void }[] = [];
  http = inject(HttpClient);

  public loadingState = signal<any>({
    loading: false,
    error: false,
    message: ''
  });

  // Built on first use, not in the constructor: the service can be created before the embed's
  // api-url is applied, and the hub must go to the same API as the REST calls.
  private connection(): signalR.HubConnection {
    if (!this.hubConnection) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.paywallService.baseUrl()}/paywallHub?TenantId=${this.paywallService.tenantId()}&LegalEntityId=${this.paywallService.legalEntityId()}&PaywallId=${this.paywallService.paywallId()}`, {
          withCredentials: false
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();
      this.handlers.forEach(({ method, handler }) => this.hubConnection!.on(method, handler));
    }
    return this.hubConnection;
  }

  // Starts the connection unless it is already started or starting.
  private connect(): Promise<void> {
    const connection = this.connection();
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      this.starting = connection.start().catch(error => {
        console.error('Error connecting to SignalR hub:', error);
        this.starting = undefined;
        throw error;
      });
    }
    return this.starting ?? Promise.resolve();
  }

  purchaseSubscription(data: any): Observable<PurchaseSubscriptionResponse> {
    const headers: Record<string, string> = {
      'PaywallId': this.paywallService.paywallId(),
      'LegalEntityId': this.paywallService.legalEntityId(),
      'TenantId': this.paywallService.tenantId(),
      'Email': data.Email,
      'X-Api-Version': packageJson.version
    };
    const previewToken = this.paywallService.previewToken();
    if (previewToken) {
      headers[PREVIEW_TOKEN_HEADER] = previewToken;
    }
    return this.http.post<string>(`${this.paywallService.baseUrl()}/subscriptions/actions/purchase`, data, {
      headers,
      observe: 'response'
    }).pipe(
      map(response => ({
        sessionId: response.body ?? '',
        sessionToken: response.headers.get(SESSION_TOKEN_HEADER)
      }))
    );
  }

  contactSales(lead: ContactSalesLead): Observable<{ leadId: string }> {
    return this.http.post<{ leadId: string }>(`${this.paywallService.baseUrl()}/leads/actions/contact-sales`, {
      Email: lead.email,
      FirstName: lead.firstName,
      LastName: lead.lastName,
      Company: lead.company,
      Phone: lead.phone,
      Seats: lead.seats,
      Note: lead.note,
      Context: {
        PageUri: lead.context.pageUri,
        PageName: lead.context.pageName,
        Hutk: lead.context.hutk
      }
    }, {
      headers: {
        'PaywallId': this.paywallService.paywallId(),
        'LegalEntityId': this.paywallService.legalEntityId(),
        'TenantId': this.paywallService.tenantId(),
        'X-Api-Version': packageJson.version
      }
    });
  }

  validateVat(vatNumber: string, countryId: string): Observable<ValidateVatResponse> {
    return this.http.post<ValidateVatResponse>(`${this.paywallService.baseUrl()}/vat/actions/validate`, {
      vatNumber,
      countryId
    }, {
      headers: {
        'PaywallId': this.paywallService.paywallId(),
        'LegalEntityId': this.paywallService.legalEntityId(),
        'TenantId': this.paywallService.tenantId(),
        'X-Api-Version': packageJson.version
      }
    });
  }

  completePurchaseSubscription(): Observable<any> {
    const sessionToken = localStorage.getItem('sessionToken');

    return this.http.post(`${this.paywallService.baseUrl()}/payments/actions/complete`, {}, {
      headers: {
        'PaywallId': this.paywallService.paywallId(),
        'LegalEntityId': this.paywallService.legalEntityId(),
        'TenantId': this.paywallService.tenantId(),
        'SessionId': localStorage.getItem('sessionId') || '',
        'X-Api-Version': packageJson.version,
        ...(sessionToken ? { [SESSION_TOKEN_HEADER]: sessionToken } : {})
      }
    });
  }

  cancelPurchaseSubscription(): Observable<any> {
    const sessionId = localStorage.getItem('sessionId') || '';
    const sessionToken = localStorage.getItem('sessionToken');

    return this.http.post(`${this.paywallService.baseUrl()}/payments/actions/cancel`, {}, {
      headers: {
        'PaywallId': this.paywallService.paywallId(),
        'LegalEntityId': this.paywallService.legalEntityId(),
        'TenantId': this.paywallService.tenantId(),
        'SessionId': sessionId,
        'X-Api-Version': packageJson.version,
        ...(sessionToken ? { [SESSION_TOKEN_HEADER]: sessionToken } : {})
      }
    }).pipe(
      // Only if no new checkout was started meanwhile, which stored its own session.
      tap(() => {
        if (localStorage.getItem('sessionId') === sessionId) {
          localStorage.removeItem('sessionId');
          localStorage.removeItem('sessionToken');
        }
      })
    );
  }

  /**
   * The browser restored the page from its back/forward cache, e.g. the buyer pressed back on the Stripe page.
   * Leaving the page closed the hub connection, so the next Buy now would never hear back. The checkout sent to
   * Stripe is still open: close it like a return to #cancel, so Buy now starts a new one.
   */
  resumeAfterPageRestore(): void {
    this.hubConnection?.stop().catch(() => undefined);
    this.hubConnection = undefined;
    this.starting = undefined;
    this.loadingState.set({ loading: false, error: false, message: '' });

    if (localStorage.getItem('sessionId')) {
      this.cancelPurchaseSubscription().subscribe({
        error: (error: unknown) => console.error('Could not cancel the checkout:', error)
      });
    }
  }

  startConnection(): Observable<void> {
    return defer(() => this.connect());
  }

  stopConnection(): void {
    this.hubConnection?.stop();
  }

  receiveMessage(methodCall: string): Observable<any> {
    return new Observable<any>((observer) => {
      const subscription = { method: methodCall, handler: (message: any) => observer.next(message) };
      this.handlers.push(subscription);
      this.hubConnection?.on(methodCall, subscription.handler);

      return () => {
        this.handlers.splice(this.handlers.indexOf(subscription), 1);
        this.hubConnection?.off(methodCall, subscription.handler);
      };
    });
  }

  // Connects first if needed: after a back/forward cache restore the connection is a new one, not yet started.
  sendMessage(method: string, message?: any): Observable<void> {
    return defer(async () => {
      await this.connect();
      if (message) {
        await this.connection().invoke(method, message);
      }
      else {
        await this.connection().invoke(method);
      }
    });
  }
}
